import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { api, apiBlob } from '../api/client';
import { money, date } from '../lib/format';

const todayStr = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;

export default function Statements() {
  const { activeId, activeBusiness } = useBusiness();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [mode, setMode] = useState('outstanding');
  const [asOf, setAsOf] = useState(todayStr());
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayStr());
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendNote, setSendNote] = useState(null);

  useEffect(() => {
    api(`/customers?business_id=${activeId}`)
      .then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false)))
      .catch(() => setCustomers([]));
    setCustomerId(''); setSt(null);
  }, [activeId]);

  const queryString = useCallback(() => {
    const p = new URLSearchParams({ business_id: String(activeId), customer_id: String(customerId), mode });
    if (mode === 'outstanding') p.set('as_of', asOf); else { p.set('from', from); p.set('to', to); }
    return p.toString();
  }, [activeId, customerId, mode, asOf, from, to]);

  const preview = useCallback(async () => {
    if (!customerId) return;
    setLoading(true); setError(null); setSendNote(null);
    try {
      const d = await api(`/statements?${queryString()}`);
      setSt(d); setSendTo(d.customer?.email || '');
    } catch (e) { setError(e?.detail || 'Could not build the statement.'); setSt(null); }
    finally { setLoading(false); }
  }, [customerId, queryString]);

  // Auto-preview when the customer or mode changes; date tweaks use the Preview button.
  useEffect(() => { if (customerId) preview(); /* eslint-disable-next-line */ }, [customerId, mode]);

  async function downloadPdf() {
    if (!customerId) return;
    setPdfBusy(true); setSendNote(null);
    try {
      const blob = await apiBlob(`/statements/pdf?${queryString()}&download=1`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `statement-${(st?.customer?.name || 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { setSendNote({ ok: false, text: 'Could not generate the PDF.' }); }
    finally { setPdfBusy(false); }
  }

  async function send() {
    if (!sendTo.trim()) { setSendNote({ ok: false, text: 'Enter a recipient email.' }); return; }
    setSending(true); setSendNote(null);
    try {
      const body = {
        business_id: activeId, customer_id: Number(customerId), mode,
        to_email: sendTo.trim(),
        cc: sendCc.trim() ? sendCc.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        message: sendMessage.trim() || undefined,
      };
      if (mode === 'outstanding') body.as_of = asOf; else { body.from = from; body.to = to; }
      const r = await api('/statements/send', { method: 'POST', body });
      if (r.emailed) setSendNote({ ok: true, text: `Statement emailed to ${r.to}.` });
      else if (r.reason === 'not_configured') setSendNote({ ok: false, text: 'Email isn’t configured for this business yet.' });
      else setSendNote({ ok: false, text: 'Could not send the statement.' });
    } catch (e) { setSendNote({ ok: false, text: e?.detail === 'no_recipient' ? 'This customer has no email on file.' : 'Could not send the statement.' }); }
    finally { setSending(false); }
  }

  const cols = [['current', 'Current'], ['d30', '1–30'], ['d60', '31–60'], ['d90', '61–90'], ['d90p', '90+']];

  return (
    <>
      <PageHeader title="Customer statements" subtitle={activeBusiness?.name} />

      <section className="panel panel--pad">
        <div className="rep-controls">
          <div className="field">
            <label>Customer</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">Select a customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Statement type</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="outstanding">Outstanding invoices</option>
              <option value="activity">Activity (date range)</option>
            </select>
          </div>
          {mode === 'outstanding' ? (
            <div className="field"><label>As of</label><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
          ) : (
            <>
              <div className="field"><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
              <div className="field"><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            </>
          )}
          <div className="rep-controls__btns">
            <button className="btn btn--primary" onClick={preview} disabled={loading || !customerId}>{loading ? 'Building…' : 'Preview'}</button>
            <button className="btn" onClick={downloadPdf} disabled={pdfBusy || !st}>Download PDF</button>
          </div>
        </div>
        {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
      </section>

      {st && (
        <section className="panel panel--pad">
          <div className="rep-head">
            <h2 className="rep-title">{st.customer?.name}</h2>
            <div className="rep-sub">
              {mode === 'outstanding' ? `Outstanding as of ${date(st.as_of)}` : `${date(st.from)} – ${date(st.to)}`}
              {' · '}Amount due <strong style={{ color: 'var(--accent)' }}>{money(st.amount_due)}</strong>
            </div>
          </div>

          {mode === 'activity' ? (
            <table className="rep-table">
              <thead><tr><th>Date</th><th>Transaction</th><th className="num">Charges</th><th className="num">Payments</th><th className="num">Balance</th></tr></thead>
              <tbody>
                <tr><td /><td><em>Opening balance</em></td><td /><td /><td className="num">{money(st.opening_balance)}</td></tr>
                {st.transactions.map((t, i) => (
                  <tr key={i}>
                    <td>{date(t.date)}</td><td>{t.label}</td>
                    <td className="num">{t.charge ? money(t.charge) : ''}</td>
                    <td className="num" style={{ color: t.payment ? 'var(--green)' : undefined }}>{t.payment ? '-' + money(t.payment) : ''}</td>
                    <td className="num">{money(t.balance)}</td>
                  </tr>
                ))}
                {st.transactions.length === 0 && <tr><td colSpan={5} className="hint" style={{ padding: '10px' }}>No activity in this period.</td></tr>}
              </tbody>
              <tfoot><tr><td colSpan={4}>Balance due</td><td className="num">{money(st.amount_due)}</td></tr></tfoot>
            </table>
          ) : (
            <table className="rep-table">
              <thead><tr><th>Date</th><th>Invoice</th><th className="num">Due</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {st.outstanding.map((o, i) => (
                  <tr key={i}><td>{date(o.date)}</td><td>#{o.reference}</td><td className="num">{date(o.due_date)}</td><td className="num">{money(o.amount)}</td></tr>
                ))}
                {st.outstanding.length === 0 && <tr><td colSpan={4} className="hint" style={{ padding: '10px' }}>No outstanding invoices.</td></tr>}
              </tbody>
              <tfoot><tr><td colSpan={3}>Total due</td><td className="num">{money(st.amount_due)}</td></tr></tfoot>
            </table>
          )}

          {st.aging && (
            <div className="stmt-aging">
              {cols.map(([k, l]) => (
                <div key={k} className="stmt-aging__cell"><span className="stmt-aging__lbl">{l}</span><span className="num">{money(st.aging[k])}</span></div>
              ))}
            </div>
          )}

          <div className="stmt-send">
            <h3 className="rep-section">Email statement</h3>
            <div className="form-grid">
              <div className="field"><label>To</label><input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="customer@example.com" /></div>
              <div className="field"><label>Cc (comma-separated)</label><input type="text" value={sendCc} onChange={(e) => setSendCc(e.target.value)} placeholder="optional" /></div>
            </div>
            <div className="field"><label>Message (optional)</label><textarea rows={2} value={sendMessage} onChange={(e) => setSendMessage(e.target.value)} placeholder="Adds a short note above the statement summary." /></div>
            <button className="btn btn--primary" onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send statement'}</button>
            {sendNote && <div className={sendNote.ok ? 'form-note' : 'form-error'} style={{ marginTop: 12 }}>{sendNote.text}</div>}
          </div>
        </section>
      )}
    </>
  );
}
